import { useStore } from '@/plugins/vuex';
import { computed, defineComponent, inject, onMounted, ref, watch } from 'vue';
import ProjectService from '@/sections/admin/entities/project/project.service';
import ProjectsList from './projects-list/ProjectsList.vue';
import ProjectsFilter from './projects-filter/ProjectsFilter.vue';
import Filters from '@/shared/model/filter/filters.model';
import SelectionMultipleFilter from '@/shared/model/filter/single/selection-multiple-filter.model';
import TextFilter from '@/shared/model/filter/single/text-filter.model';
import FilterGroupOr from '@/shared/model/filter/group/filter-group-or.model';
import { Projects } from '@/shared/model/project.model';
import CompanyService from '@/sections/admin/entities/company/company.service';

export default defineComponent({
  components: {
    ProjectsList,
    ProjectsFilter
  },
  setup() {
    const companyService = inject('companyService') as CompanyService;
    const companies = ref<Array<{ id: string; desc: string }>>([]);
    const store = useStore();
    const projectService: ProjectService = inject('projectService');
    const projects = ref<Projects>(Projects.empty());
    const filteredProjects = ref<Projects>(Projects.empty());
    const isLoading = ref(true);
    const displaySkeleton = computed(() => {
      return isLoading.value || (projects.value.length === 0 && isLoading.value);
    });
    const filters = ref<Filters>(new Filters('AND'));

    onMounted(() => {
      retrieveCompanies();
      retrieveAllProjectsByManager().then(() => {
        filteredProjects.value = filters.value.filter(projects.value) as Projects;
      });
    });

    watch(
      () => filters.value,
      f => {
        filteredProjects.value = f.filter(projects.value) as Projects;
      },
      { deep: true }
    );

    const retrieveCompanies = () => {
      if (companyService && typeof companyService.retrieve === 'function') {
        companyService.retrieve().then((res: any) => {
          companies.value = (res || []).map((c: any) => ({
            id: c.cif || c.id,
            desc: c.name || c.desc
          }));
        });
      }
    };

    const retrieveAllProjectsByManager = () => {
      isLoading.value = true;
      return projectService
        .retrieveAll()
        .then(res => {
          projects.value = Projects.from(res.data);
          filteredProjects.value = Projects.from(res.data);
          isLoading.value = false;
        })
        .catch(err => {
          console.error(err);
        });
    };

    const handleCompanyChange = companyIds => {
      filters.value.add(new SelectionMultipleFilter('companyCif', companyIds));
      store.commit('pagesStore/manager/projects/setCompanyFilter', companyIds);
    };

    const handleSearchChange = searchTerm => {
      const groupOr = new FilterGroupOr('searchText');
      groupOr.add(new TextFilter('name', searchTerm));
      filters.value.add(groupOr);
      store.commit('pagesStore/manager/projects/setSearchFilter', searchTerm);
    };

    return {
      filteredProjects,
      projects,
      displaySkeleton,
      companies,
      handleCompanyChange,
      handleSearchChange,
      retrieveCompanies
    };
  }
});
